import { useMemo } from 'react'
import {
  ScheduleAssignment,
  addDays,
  formatDateISO,
  formatTimeShort,
  isWeekend,
  pickTextColor,
  startOfWeek,
  WEEKDAYS_PL,
  WEEKDAYS_PL_LONG,
} from './utils'

interface Props {
  anchor: Date
  assignments: ScheduleAssignment[]
  canManage: boolean
  currentUserId: number | null
  onEdit: (a: ScheduleAssignment) => void
  onAdd: (defaultDate?: string) => void
  onDelete: (id: number) => void
}

export default function ScheduleWeekView({
  anchor, assignments, canManage, currentUserId, onEdit, onAdd, onDelete,
}: Props) {
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor])
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart])

  // grupuj wpisy per dzień
  const byDay = useMemo(() => {
    const map: Record<string, ScheduleAssignment[]> = {}
    for (const a of assignments) {
      const key = a.date
      if (!map[key]) map[key] = []
      map[key].push(a)
    }
    // sortuj wewnątrz dnia po godzinie startu
    for (const k of Object.keys(map)) {
      map[k].sort((x, y) => {
        const tx = x.effective_start_time || ''
        const ty = y.effective_start_time || ''
        return tx.localeCompare(ty)
      })
    }
    return map
  }, [assignments])

  const today = formatDateISO(new Date())

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Desktop: 7 kolumn */}
      <div className="hidden md:grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {days.map((d, i) => {
          const iso = formatDateISO(d)
          const isToday = iso === today
          return (
            <div
              key={iso}
              className={`px-2 py-2 text-sm border-r last:border-r-0 border-gray-200 ${
                isWeekend(d) ? 'bg-gray-100' : ''
              } ${isToday ? 'bg-blue-50' : ''}`}
            >
              <div className="font-semibold text-gray-700">
                {WEEKDAYS_PL[i]}
                {isToday && <span className="ml-1 text-blue-600 text-xs">(dziś)</span>}
              </div>
              <div className="text-xs text-gray-500">
                {String(d.getDate()).padStart(2, '0')}.{String(d.getMonth() + 1).padStart(2, '0')}
              </div>
            </div>
          )
        })}
      </div>

      <div className="hidden md:grid grid-cols-7 min-h-[300px]">
        {days.map(d => {
          const iso = formatDateISO(d)
          const items = byDay[iso] || []
          return (
            <div
              key={iso}
              className={`border-r last:border-r-0 border-gray-200 p-1.5 space-y-1.5 ${
                isWeekend(d) ? 'bg-gray-50/60' : ''
              }`}
            >
              {items.length === 0 && (
                <div className="text-xs text-gray-300 italic">—</div>
              )}
              {items.map(a => (
                <AssignmentCard
                  key={a.id}
                  a={a}
                  canManage={canManage}
                  isMine={a.user_id === currentUserId}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
              {canManage && (
                <button
                  onClick={() => onAdd(iso)}
                  className="w-full text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded py-1 border border-dashed border-gray-200 hover:border-blue-300"
                  title="Dodaj wpis"
                >
                  + dodaj
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Mobile: lista dni jako sekcje */}
      <div className="md:hidden divide-y divide-gray-200">
        {days.map((d, i) => {
          const iso = formatDateISO(d)
          const items = byDay[iso] || []
          const isToday = iso === today
          return (
            <div key={iso} className={`p-3 ${isWeekend(d) ? 'bg-gray-50' : ''} ${isToday ? 'bg-blue-50' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-gray-800">
                    {WEEKDAYS_PL_LONG[i]}
                    {isToday && <span className="ml-1 text-blue-600 text-xs">(dziś)</span>}
                  </div>
                  <div className="text-xs text-gray-500">
                    {String(d.getDate()).padStart(2, '0')}.{String(d.getMonth() + 1).padStart(2, '0')}.{d.getFullYear()}
                  </div>
                </div>
                {canManage && (
                  <button
                    onClick={() => onAdd(iso)}
                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                  >
                    + dodaj
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {items.length === 0 && <div className="text-xs text-gray-400 italic">brak wpisów</div>}
                {items.map(a => (
                  <AssignmentCard
                    key={a.id}
                    a={a}
                    canManage={canManage}
                    isMine={a.user_id === currentUserId}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AssignmentCard({
  a, canManage, isMine, onEdit, onDelete,
}: {
  a: ScheduleAssignment
  canManage: boolean
  isMine: boolean
  onEdit: (a: ScheduleAssignment) => void
  onDelete: (id: number) => void
}) {
  const bg = a.effective_color || '#3b82f6'
  const fg = pickTextColor(bg)
  const isOnCall = a.effective_category === 'oncall'
  const isLeave = a.effective_category === 'leave'

  const time =
    a.effective_start_time && a.effective_end_time
      ? `${formatTimeShort(a.effective_start_time)}–${formatTimeShort(a.effective_end_time)}`
      : ''

  return (
    <div
      className={`group rounded px-2 py-1.5 shadow-sm relative ${
        isOnCall ? 'border-2 border-dashed' : 'border'
      } ${isMine ? 'ring-1 ring-yellow-400' : ''}`}
      style={{
        backgroundColor: isLeave ? `${bg}22` : bg,
        borderColor: bg,
        color: isLeave ? '#374151' : fg,
      }}
      title={a.note || a.effective_label}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <div className="text-[11px] leading-tight font-semibold truncate">{a.effective_label}</div>
          <div className="text-[11px] leading-tight truncate">
            {a.user_name || a.user_username || `#${a.user_id}`}
          </div>
          {time && (
            <div className="text-[10px] leading-tight opacity-90">
              {time}
              {isOnCall && <span className="ml-1">🟠</span>}
            </div>
          )}
        </div>
        {canManage && (
          <div className="opacity-0 group-hover:opacity-100 transition flex flex-col gap-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(a) }}
              className="text-[10px] px-1 rounded bg-white/30 hover:bg-white/50"
              style={{ color: isLeave ? '#374151' : fg }}
              title="Edytuj"
            >✏️</button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(a.id) }}
              className="text-[10px] px-1 rounded bg-white/30 hover:bg-white/50"
              style={{ color: isLeave ? '#374151' : fg }}
              title="Usuń"
            >🗑️</button>
          </div>
        )}
      </div>
      {a.note && <div className="text-[10px] opacity-80 truncate mt-0.5">📝 {a.note}</div>}
    </div>
  )
}
